import { useEffect, useState, useCallback, useRef } from 'react';
import {
    ReactFlow,
    Controls,
    Background,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    MiniMap,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import useFileStore from '../../stores/fileStore';
import './CanvasEditor.css';

// Custom node types
import CharacterNode from './nodes/CharacterNode';
import LocationNode from './nodes/LocationNode';
import PlotPointNode from './nodes/PlotPointNode';
import NoteNode from './nodes/NoteNode';

const nodeTypes = {
    character: CharacterNode,
    location: LocationNode,
    plotpoint: PlotPointNode,
    note: NoteNode,
};

const defaultEdgeOptions = {
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#f4a261', strokeWidth: 2 },
};

function CanvasEditor({ projectId, path }) {
    const { t } = useTranslation();
    const { updateFileContent, markUnsaved, markSaved } = useFileStore();
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const hasUserEdited = useRef(false);

    // Read canvas content reactively from Convex DB (survives reload)
    const files = useQuery(api.files.listByProject, projectId ? { projectId } : 'skip');
    const canvasFile = files?.find((f) => f.path === path);
    const dbContent = canvasFile?.content || '';

    // Load from DB when content arrives — but don't overwrite user edits
    useEffect(() => {
        if (!dbContent || hasUserEdited.current) return;
        try {
            const data = JSON.parse(dbContent);
            setNodes(data.nodes || []);
            setEdges(data.edges || []);
        } catch {
            setNodes([]);
            setEdges([]);
        }
    }, [dbContent]);

    // Save canvas data
    const saveCanvas = useCallback((newNodes, newEdges) => {
        hasUserEdited.current = true;
        markUnsaved(path);
        const data = JSON.stringify({ nodes: newNodes, edges: newEdges }, null, 2);
        updateFileContent(projectId, path, data);
        markSaved(path);
    }, [path, projectId, markUnsaved, markSaved, updateFileContent]);

    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => {
            const newNodes = applyNodeChanges(changes, nds);
            saveCanvas(newNodes, edges);
            return newNodes;
        });
    }, [edges, saveCanvas]);

    const onEdgesChange = useCallback((changes) => {
        setEdges((eds) => {
            const newEdges = applyEdgeChanges(changes, eds);
            saveCanvas(nodes, newEdges);
            return newEdges;
        });
    }, [nodes, saveCanvas]);

    const onConnect = useCallback((connection) => {
        setEdges((eds) => {
            const newEdges = addEdge(connection, eds);
            saveCanvas(nodes, newEdges);
            return newEdges;
        });
    }, [nodes, saveCanvas]);

    // Add new node
    const addNode = (type) => {
        const newNode = {
            id: `${type}-${Date.now()}`,
            type,
            position: { x: Math.random() * 500, y: Math.random() * 300 },
            data: {
                label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
                description: ''
            },
        };
        setNodes((nds) => {
            const newNodes = [...nds, newNode];
            saveCanvas(newNodes, edges);
            return newNodes;
        });
    };

    return (
        <div className="canvas-editor">
            <div className="canvas-toolbar">
                <span className="toolbar-label">{t('editor.canvas.addNode')}</span>
                <button className="btn btn-sm btn-ghost" onClick={() => addNode('character')}>
                    <span>👤</span> {t('editor.canvas.character')}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => addNode('location')}>
                    <span>📍</span> {t('editor.canvas.location')}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => addNode('plotpoint')}>
                    <span>⚡</span> {t('editor.canvas.plotPoint')}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => addNode('note')}>
                    <span>📝</span> {t('editor.canvas.note')}
                </button>
            </div>

            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                fitView
                proOptions={{ hideAttribution: true }}
            >
                <Controls position="bottom-right" />
                <MiniMap
                    nodeColor={(node) => {
                        if (node.type === 'character') return '#f472b6';
                        if (node.type === 'location') return '#38bdf8';
                        if (node.type === 'plotpoint') return '#a3e635';
                        return '#fbbf24';
                    }}
                    maskColor="rgba(10, 13, 18, 0.8)"
                    style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-surface-border)',
                        borderRadius: 'var(--radius-md)'
                    }}
                />
                <Background color="#232a36" gap={24} size={1} />
            </ReactFlow>
        </div>
    );
}

export default CanvasEditor;
